import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { ReactComponent as ArrowRightIcon } from "Components/_Shared/Icons/Assets/arrow-right.svg";
import { useListAnalyzerPartners, useListInsuranceContracts } from "Infrastructure/ApiZfp/AnalyzerApi";
import { type Contract, ContractContractType, ListAnalyzerPartnersToolType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useContext, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { ClientSolutionTile } from "./ClientSolutionTile";
import { InsuranceContractDrawer } from "./InsuranceContractDrawer";

type Props = {
  analysisId: string;
  versionId: string;
  clientId: string;
};

export const ClientSolution: FC<Props> = ({ analysisId, versionId, clientId }) => {
  const theme = useTheme();

  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();

  const [insuranceContractDrawerOpen, setInsuranceContractDrawerOpen] = useState(false);
  const [editedContract, setEditedContract] = useState<Contract>();
  const [editedContractType, setEditedContractType] = useState<ContractContractType>(ContractContractType.current);

  const { data: insuranceContracts } = useListInsuranceContracts(analysisId, versionId, clientId);
  const { data: insurancePartnersData } = useListAnalyzerPartners({ toolType: ListAnalyzerPartnersToolType.insurance });

  return (
    <>
      <Stack spacing={Spacing.XL}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t(Resources.AnalysisDetail.Insurance.ClientSolution)}
        </Typography>
        <Stack spacing={Spacing.L}>
          <Typography variant="body2" fontWeight={500}>
            {t(Resources.AnalysisDetail.Insurance.ExistingSolution)}
          </Typography>
          {insuranceContracts
            ?.filter((ic) => ic.contractType === ContractContractType.current)
            .map((ic) => (
              <ClientSolutionTile
                key={ic.guid}
                clientId={clientId}
                insuranceContract={ic}
                partnerName={insurancePartnersData?.find((p) => p.id === ic.partnerId)?.name ?? ""}
                selected={ic.createRecommended}
                onEdit={() => {
                  setEditedContract(ic);
                  setInsuranceContractDrawerOpen(true);
                }}
              />
            ))}
          <Button
            variant="text"
            colorCustom={theme.colors.brandPrimary}
            sx={{ width: "fit-content", marginLeft: "-16px !important" }}
            startAdornment={<AddIcon />}
            onClick={() => {
              setEditedContract(undefined);
              setEditedContractType(ContractContractType.current);
              setInsuranceContractDrawerOpen(true);
            }}
          >
            {t(Resources.AnalysisDetail.AssetsAndContracts.AddCurrentSolution)}
          </Button>
        </Stack>
        <Stack spacing={Spacing.L}>
          <Typography variant="body2" fontWeight={500}>
            {t(Resources.AnalysisDetail.Insurance.RecommendedSolution)}
          </Typography>
          {insuranceContracts
            ?.filter((ic) => ic.contractType === ContractContractType.recommended)
            .map((ic) => (
              <ClientSolutionTile
                key={ic.guid}
                insuranceContract={ic}
                clientId={clientId}
                partnerName={insurancePartnersData?.find((p) => p.id === ic.partnerId)?.name ?? ""}
                selected={ic.createRecommended}
                onEdit={() => {
                  setEditedContract(ic);
                  setInsuranceContractDrawerOpen(true);
                }}
              />
            ))}
          <Button
            variant="text"
            colorCustom={theme.colors.brandPrimary}
            sx={{ width: "fit-content", marginLeft: "-16px !important" }}
            startAdornment={<AddIcon />}
            onClick={() => {
              setEditedContract(undefined);
              setEditedContractType(ContractContractType.recommended);
              setInsuranceContractDrawerOpen(true);
            }}
          >
            {t(Resources.AnalysisDetail.Insurance.AddSolution)}
          </Button>
        </Stack>
        <Box display="flex" justifyContent="flex-end">
          <Button
            onClick={() => window.open("https://zfp.prod1.nsure.cz", "_blank", "noopener,noreferrer")}
            disabled={readOnly}
            color="secondary"
            endAdornment={<ArrowRightIcon width={20} height={20} />}
          >
            {t(Resources.AnalysisDetail.Insurance.GoToLifeUp)}
          </Button>
        </Box>
      </Stack>
      <InsuranceContractDrawer
        analysisId={analysisId}
        versionId={versionId}
        insuranceContractType={editedContract ? editedContract.contractType : editedContractType}
        insuranceContract={editedContract}
        open={insuranceContractDrawerOpen}
        clientId={clientId}
        onClose={() => {
          setEditedContract(undefined);
          setInsuranceContractDrawerOpen(false);
        }}
      />
    </>
  );
};
